import { useClinicalRecordLock } from '../../../../components/clinical-record-lock';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from '../../../../components/snackbar';
import { DEService } from '../../../../api/clinical-pathways/des.service';
import {
  RHFCheckbox,
  RHFSelect,
  RHFTextField,
} from '../../../../components/hook-form';
import FormProvider from '../../../../components/hook-form/FormProvider';
import ImagingUploadSection from '../../../../components/image-upload/ImagingUploadSection';

import { UploadService } from '../../../../api/upload.service';
import { useAppContext } from '../../../../providers/AppContext';

// Define form values type
type FormValuesProps = {
  // Patient & Admin Data
  diabetesType: string;
  diabetesDuration: string;
  referralSource: string;
  gpContact: string;

  // Medical & Diabetes History
  recentHba1c: string;
  medications: string;
  hasHypertension: boolean;
  hasKidneyDisease: boolean;
  smokingStatus: string;

  // Ocular History
  previousDiabeticEyeDisease: string;
  previousLaserTreatment: string;
  cataractSurgery: string;
  otherOcularConditions: string;

  // Visual Acuity
  distanceVARight: string;
  distanceVALeft: string;
  distanceVABinocular: string;
  nearVARight: string;
  nearVALeft: string;
  pinholeVARight: string;
  pinholeVALeft: string;

  // Pupillary Examination
  pupilSize: string;
  pupilReaction: string;
  rapd: string;

  // Anterior Segment
  lidsAndConjunctiva: string;
  cornea: string;
  lens: string;
  iris: string;
  iopRight: string;
  iopLeft: string;

  // Fundus Examination
  pupilDilation: string;
  fundusFindings: string;

  // Imaging
  fundusPhotography: string;
  octFindings: string;
  fundusPhotos?: string[];
  octImages?: string[];

  // Grading
  retinopathyGrade: string;
  maculopathyGrade: string;

  // Additional Notes
  cataractAffectingView: boolean;
  poorImageQuality: string;
  otherFindings: string;

  // Clinical Impression
  clinicalImpression: string;
  reviewTimeline: string;
  referralRequired: boolean;
  referralNotes: string;
};

const DIABETES_TYPES = [
  { value: 'type1', label: 'Type 1' },
  { value: 'type2', label: 'Type 2' },
];

// Referral source handled in Info tab

const SMOKING_STATUS = [
  { value: 'never', label: 'Never Smoked' },
  { value: 'current', label: 'Current Smoker' },
  { value: 'former', label: 'Former Smoker' },
];

const GRADING_OPTIONS = [
  { value: 'r0', label: 'R0 - No retinopathy' },
  { value: 'r1', label: 'R1 - Background retinopathy' },
  { value: 'r2', label: 'R2 - Pre-proliferative' },
  { value: 'r3', label: 'R3 - Proliferative' },
  { value: 'm0', label: 'M0 - No maculopathy' },
  { value: 'm1', label: 'M1 - Maculopathy present' },
];

const REVIEW_TIMELINES = [
  { value: '1y', label: '1 Year' },
  { value: '6m', label: '6 Months' },
  { value: '3m', label: '3 Months' },
  { value: 'urgent', label: 'Urgent Referral' },
];

type Props = {
  clinicalRecordId?: number;
  desRecord?: any;
  onUpdate?: () => void;
};

function DiabeticEyeScreeningForm({
  clinicalRecordId,
  desRecord,
  onUpdate,
}: Props) {
  const lock = useClinicalRecordLock();
  const [currentSection, setCurrentSection] = useState(0);
  const [fundusPreviews, setFundusPreviews] = useState<string[]>([]);
  const [octPreviews, setOctPreviews] = useState<string[]>([]);
  const { patientId } = useParams();
  const { getAllClinicalRecords } = useAppContext();

  const sections = [
    { id: 'medical', label: 'Medical History' },
    { id: 'ocular', label: 'Ocular History' },
    { id: 'examination', label: 'Examination' },
    { id: 'imaging', label: 'Imaging' },
    { id: 'grading', label: 'Grading' },
    { id: 'impression', label: 'Clinical Impression' },
  ];

  const defaultValues = {
    // Initialize with default values
    diabetesType: '',
    diabetesDuration: '',
    recentHba1c: '',
    medications: '',
    hasHypertension: false,
    hasKidneyDisease: false,
    smokingStatus: '',
    previousDiabeticEyeDisease: '',
    previousLaserTreatment: '',
    cataractSurgery: '',
    otherOcularConditions: '',
    distanceVARight: '',
    distanceVALeft: '',
    distanceVABinocular: '',
    nearVARight: '',
    nearVALeft: '',
    pinholeVARight: '',
    pinholeVALeft: '',
    pupilSize: '',
    pupilReaction: '',
    rapd: '',
    lidsAndConjunctiva: '',
    cornea: '',
    lens: '',
    iris: '',
    iopRight: '',
    iopLeft: '',
    pupilDilation: '',
    fundusFindings: '',
    fundusPhotography: '',
    octFindings: '',
    fundusPhotos: [],
    octImages: [],
    retinopathyGrade: '',
    maculopathyGrade: '',
    cataractAffectingView: false,
    poorImageQuality: '',
    otherFindings: '',
    clinicalImpression: '',
    reviewTimeline: '',
    referralRequired: false,
    referralNotes: '',
  };

  const methods = useForm<FormValuesProps>({
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  const sectionDefaults: Record<string, Partial<FormValuesProps>> = {
    medical: {
      diabetesType: '',
      diabetesDuration: '',
      recentHba1c: '',
      medications: '',
      hasHypertension: false,
      hasKidneyDisease: false,
      smokingStatus: '',
    },
    ocular: {
      previousDiabeticEyeDisease: '',
      previousLaserTreatment: '',
      cataractSurgery: '',
      otherOcularConditions: '',
    },
    examination: {
      distanceVARight: '',
      distanceVALeft: '',
      distanceVABinocular: '',
      nearVARight: '',
      nearVALeft: '',
      pinholeVARight: '',
      pinholeVALeft: '',
      pupilSize: '',
      pupilReaction: '',
      rapd: '',
      lidsAndConjunctiva: '',
      cornea: '',
      lens: '',
      iris: '',
      iopRight: '',
      iopLeft: '',
      pupilDilation: '',
      fundusFindings: '',
    },
    imaging: {
      fundusPhotography: '',
      octFindings: '',
      fundusPhotos: [],
      octImages: [],
    },
    grading: {
      retinopathyGrade: '',
      maculopathyGrade: '',
      cataractAffectingView: false,
      poorImageQuality: '',
      otherFindings: '',
    },
    impression: {
      clinicalImpression: '',
      reviewTimeline: '',
      referralRequired: false,
      referralNotes: '',
    },
  };

  const handleResetSection = () => {
    const id = sections[currentSection].id;
    const defaults = sectionDefaults[id] || {};
    reset({
      ...methods.getValues(),
      ...defaults,
    });
    if (id === 'imaging') {
      setFundusPreviews([]);
      setOctPreviews([]);
    }
    enqueueSnackbar(`${sections[currentSection].label} reset successfully`, {
      variant: 'success',
    });
  };

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const service = new DEService();
      // Exclude system fields that shouldn't be sent to backend
      const { id, createdDate, updatedDate, ...formData } = data as any;

      const payload = {
        ...formData,
        fundusPhotos: fundusPreviews,
        octImages: octPreviews,
        clinicalRecordId,
      };
      // Amending a signed record: stage the change instead of saving it.
      if (lock.stageIfAmending('diabeticEyeScreening', payload)) return;
      if (desRecord?.id) {
        await service.updateDES(patientId!, desRecord.id, payload);
        enqueueSnackbar('DES updated successfully', { variant: 'success' });
      } else {
        await service.createDES(patientId!, payload);
        enqueueSnackbar('DES saved successfully', { variant: 'success' });
      }
      if (onUpdate) {
        await onUpdate();
      } else {
        getAllClinicalRecords(patientId);
      }
    } catch (error) {
      enqueueSnackbar('Failed to save DES', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (desRecord && desRecord.id) {
      const {
        fundusPhotos = [],
        octImages = [],
        id,
        createdDate,
        updatedDate,
        ...rest
      } = desRecord;
      reset(rest);
      setFundusPreviews(Array.isArray(fundusPhotos) ? fundusPhotos : []);
      setOctPreviews(Array.isArray(octImages) ? octImages : []);
    }
  }, [desRecord, reset]);

  const nextSection = () => {
    setCurrentSection((prev) => Math.min(prev + 1, sections.length - 1));
  };

  const prevSection = () => {
    setCurrentSection((prev) => Math.max(prev - 1, 0));
  };

  const renderSection = () => {
    switch (sections[currentSection].id) {
      case 'medical':
        return renderMedicalHistorySection();
      case 'ocular':
        return renderOcularHistorySection();
      case 'examination':
        return (
          <>
            {renderVisualAcuitySection()}
            {renderPupillaryExamSection()}
            {renderAnteriorSegmentSection()}
            {renderFundusExamSection()}
          </>
        );
      case 'imaging':
        return renderImagingSection();
      case 'grading':
        return (
          <>
            {renderGradingSection()}
            {renderAdditionalNotesSection()}
          </>
        );
      case 'impression':
        return renderClinicalImpressionSection();
      default:
        return null;
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const uploadService = new UploadService();
      const response = await uploadService.uploadFile(file);
      return response.data.url;
    } catch (error) {
      console.error('Upload failed', error);
      enqueueSnackbar('Image upload failed', { variant: 'error' });
      throw error;
    }
  };

  const renderMedicalHistorySection = () => (
    <Card sx={{ mb: 3 }} key="medical">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Medical & Diabetes History
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFSelect
              name="diabetesType"
              label="Diabetes Type"
              size="small"
              fullWidth
            >
              {DIABETES_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </RHFSelect>
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="diabetesDuration"
              label="Duration of Diabetes (years)"
              size="small"
              type="number"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="recentHba1c"
              label="Most Recent HbA1c (mmol/mol)"
              size="small"
              type="number"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFSelect
              name="smokingStatus"
              label="Smoking Status"
              size="small"
              fullWidth
            >
              {SMOKING_STATUS.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </RHFSelect>
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="medications"
              label="Current Medications"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend">Medical Conditions</FormLabel>
              <Box sx={{ display: 'flex' }}>
                <RHFCheckbox name="hasHypertension" label="Hypertension" />
                <RHFCheckbox name="hasKidneyDisease" label="Kidney Disease" />
              </Box>
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderOcularHistorySection = () => (
    <Card sx={{ mb: 3 }} key="ocular">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Ocular History
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="previousDiabeticEyeDisease"
              label="Previous Diabetic Eye Disease"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="previousLaserTreatment"
              label="Previous Laser Treatment"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="cataractSurgery"
              label="Cataract Surgery History"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="otherOcularConditions"
              label="Other Ocular Conditions"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderVisualAcuitySection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Visual Acuity
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Distance VA</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <RHFTextField
              name="distanceVARight"
              label="Right Eye (R)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <RHFTextField
              name="distanceVALeft"
              label="Left Eye (L)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <RHFTextField
              name="distanceVABinocular"
              label="Binocular"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Near VA (optional)
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="nearVARight"
              label="Right Eye (R)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="nearVALeft"
              label="Left Eye (L)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Pinhole VA (if reduced)
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="pinholeVARight"
              label="Right Eye (R)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="pinholeVALeft"
              label="Left Eye (L)"
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderPupillaryExamSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Pupillary Examination
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <RHFTextField
              name="pupilSize"
              label="Pupil Size (mm)"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <RHFTextField
              name="pupilReaction"
              label="Pupil Reaction"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <RHFTextField
              name="rapd"
              label="RAPD (if any)"
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderAnteriorSegmentSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Anterior Segment & IOP
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="lidsAndConjunctiva"
              label="Lids & Conjunctiva"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="cornea"
              label="Cornea"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="lens"
              label="Lens"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="iris"
              label="Iris"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Intraocular Pressure (IOP) - mmHg
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="iopRight"
              label="Right Eye (R)"
              size="small"
              type="number"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="iopLeft"
              label="Left Eye (L)"
              size="small"
              type="number"
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderFundusExamSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Fundus Examination
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex' }}>
          <RHFTextField
            name="pupilDilation"
            label="Pupil Dilation"
            size="small"
            fullWidth
            multiline
            rows={4}
            placeholder="Agent used and time of dilation"
            sx={{ width: '50%' }}
          />
          <RHFTextField
            name="fundusFindings"
            label="Fundus Findings"
            size="small"
            fullWidth
            multiline
            rows={4}
            placeholder="Detailed findings from fundus examination"
            sx={{ width: '50%', ml: '15px' }}
          />
        </Box>
      </CardContent>
    </Card>
  );

  const renderImagingSection = () => (
    <Card sx={{ mb: 3 }} key="imaging">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Retinal Imaging
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="fundusPhotography"
              label="Fundus Photography"
              size="small"
              fullWidth
              multiline
              rows={2}
            />

            <ImagingUploadSection
              title="Fundus Photos"
              previews={fundusPreviews}
              setPreviews={setFundusPreviews}
              maxImages={4}
              onUpload={handleImageUpload}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <RHFTextField
              name="octFindings"
              label="OCT Findings"
              size="small"
              fullWidth
              multiline
              rows={2}
            />

            <ImagingUploadSection
              title="OCT Images"
              previews={octPreviews}
              setPreviews={setOctPreviews}
              maxImages={4}
              onUpload={handleImageUpload}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderGradingSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Retinopathy & Maculopathy Grading
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFSelect
              name="retinopathyGrade"
              label="Retinopathy Grade"
              size="small"
              fullWidth
            >
              {GRADING_OPTIONS.filter((opt) => opt.value.startsWith('r')).map(
                (grade) => (
                  <MenuItem key={grade.value} value={grade.value}>
                    {grade.label}
                  </MenuItem>
                ),
              )}
            </RHFSelect>
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFSelect
              name="maculopathyGrade"
              label="Maculopathy Grade"
              size="small"
              fullWidth
            >
              {GRADING_OPTIONS.filter((opt) => opt.value.startsWith('m')).map(
                (grade) => (
                  <MenuItem key={grade.value} value={grade.value}>
                    {grade.label}
                  </MenuItem>
                ),
              )}
            </RHFSelect>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderAdditionalNotesSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Additional Notes
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RHFCheckbox
              name="cataractAffectingView"
              label="Cataract affecting view"
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField
              name="poorImageQuality"
              label="Poor Image Quality (if any)"
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField
              name="otherFindings"
              label="Other Findings"
              size="small"
              fullWidth
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderClinicalImpressionSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Clinical Impression & Management
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RHFTextField
              name="clinicalImpression"
              label="Clinical Impression"
              size="small"
              fullWidth
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFSelect
              name="reviewTimeline"
              label="Review Timeline"
              size="small"
              fullWidth
            >
              {REVIEW_TIMELINES.map((timeline) => (
                <MenuItem key={timeline.value} value={timeline.value}>
                  {timeline.label}
                </MenuItem>
              ))}
            </RHFSelect>
          </Grid>
          <Grid item xs={12}>
            <RHFCheckbox
              name="referralRequired"
              label="Referral to Ophthalmology Required"
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField
              name="referralNotes"
              label="Referral Notes (if applicable)"
              size="small"
              fullWidth
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          Diabetic Eye Screening / Monitoring
        </Typography>

        <Card>
          <Tabs
            value={currentSection}
            onChange={(_, newValue) => setCurrentSection(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              px: 2,
              bgcolor: 'background.never',
            }}
          >
            {sections.map((tab, index) => (
              <Tab
                key={tab.id}
                label={tab.label}
                value={index}
                disabled={isSubmitting}
              />
            ))}
          </Tabs>
          <Divider />
          <CardContent>
            {renderSection()}

            <Box
              sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}
            >
              <Button
                variant="outlined"
                onClick={prevSection}
                disabled={currentSection === 0 || isSubmitting}
              >
                Previous
              </Button>
              <Box sx={{ display: 'flex', gap: '10px' }}>
                <Button
                  data-lock-hide
                  variant="outlined"
                  onClick={handleResetSection}
                  disabled={isSubmitting}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={nextSection}
                  disabled={
                    currentSection === sections.length - 1 || isSubmitting
                  }
                >
                  Next
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{ ml: 2 }}
          >
            Save Changes
          </LoadingButton>
        </Box>
      </Box>
    </FormProvider>
  );
}

export default DiabeticEyeScreeningForm;
